import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { DashboardLayout } from './components/layout/DashboardLayout'
import { Toaster } from '@/components/ui/toaster'
import { setAccessToken } from '@/lib/api'
import { ErrorBoundary, RouteErrorBoundary } from '@/components/errors'
import { Spinner } from '@/components/ui/spinner'

// Note: setAccessToken is used by other modules that import from api
void setAccessToken // Prevents "unused" lint warning

// Auth components
import { ProtectedRoute, PublicRoute } from '@/components/auth'

// Onboarding components
import { useHasCompletedOnboarding } from '@/components/onboarding/useOnboarding'

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <Spinner size="xl" label="Loading application..." />
  </div>
)

// Eagerly loaded pages (critical for initial load)
import { Login, GoogleCallback, Approve } from '@/pages/auth'

// Lazy load Dashboard and Home to reduce initial bundle size
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })))

// Lazy loaded landing page
const Landing = lazy(() => import('./pages/landing').then(m => ({ default: m.Landing })))

// Lazy loaded legal pages
const Terms = lazy(() => import('./pages/legal/Terms').then(m => ({ default: m.Terms })))
const Privacy = lazy(() => import('./pages/legal/Privacy').then(m => ({ default: m.Privacy })))
const DataProcessingAgreement = lazy(() => import('./pages/legal/DataProcessingAgreement').then(m => ({ default: m.DataProcessingAgreement })))
const AccessPolicy = lazy(() => import('./pages/legal/AccessPolicy').then(m => ({ default: m.AccessPolicy })))

// Lazy loaded pricing page
const Pricing = lazy(() => import('./pages/pricing').then(m => ({ default: m.Pricing })))

// Lazy loaded security page
const Security = lazy(() => import('./pages/security'))

// Lazy loaded auth pages (less critical)
const Signup = lazy(() => import('./pages/auth/Signup').then(m => ({ default: m.Signup })))

// Lazy loaded main pages (loaded when user navigates)
const ChatMode = lazy(() => import('./pages/ChatMode'))
const Models = lazy(() => import('./pages/Models').then(m => ({ default: m.Models })))
const ModelCreate = lazy(() => import('./pages/ModelCreate').then(m => ({ default: m.ModelCreate })))
const ModelDetail = lazy(() => import('./pages/ModelDetail').then(m => ({ default: m.ModelDetail })))
const ModelMonitoring = lazy(() => import('./pages/ModelMonitoring').then(m => ({ default: m.ModelMonitoring })))
const InsightsDashboard = lazy(() => import('./pages/InsightsDashboard').then(m => ({ default: m.InsightsDashboard })))
const Predictions = lazy(() => import('./pages/Predictions').then(m => ({ default: m.Predictions })))
const PredictionHistory = lazy(() => import('./pages/PredictionHistory').then(m => ({ default: m.PredictionHistory })))
const DataSources = lazy(() => import('./pages/DataSources').then(m => ({ default: m.DataSources })))
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })))
const PixelSetup = lazy(() => import('./pages/PixelSetup').then(m => ({ default: m.PixelSetup })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const OAuthCallback = lazy(() => import('./pages/OAuthCallback').then(m => ({ default: m.OAuthCallback })))

// Lazy loaded dashboard pages
const ModelPerformance = lazy(() => import('./pages/ModelPerformance').then(m => ({ default: m.ModelPerformance })))
const DriftDashboard = lazy(() => import('./pages/DriftDashboard').then(m => ({ default: m.DriftDashboard })))
const SLADashboard = lazy(() => import('./pages/SLADashboard').then(m => ({ default: m.SLADashboard })))

// Lazy loaded admin pages (only loaded for super admins)
const SuperAdminDashboard = lazy(() => import('./pages/admin/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })))
const TenantsList = lazy(() => import('./pages/admin/TenantsList').then(m => ({ default: m.TenantsList })))
const TenantDetail = lazy(() => import('./pages/admin/TenantDetail').then(m => ({ default: m.TenantDetail })))
const TenantCreateForm = lazy(() => import('./pages/admin/TenantCreateForm').then(m => ({ default: m.TenantCreateForm })))
const PlansManagement = lazy(() => import('./pages/admin/PlansManagement').then(m => ({ default: m.PlansManagement })))
const GlobalUserSearch = lazy(() => import('./pages/admin/GlobalUserSearch').then(m => ({ default: m.GlobalUserSearch })))
const FeedbackList = lazy(() => import('./pages/admin/FeedbackList').then(m => ({ default: m.FeedbackList })))
const FeedbackDetail = lazy(() => import('./pages/admin/FeedbackDetail').then(m => ({ default: m.FeedbackDetail })))
const FeedbackCreate = lazy(() => import('./pages/admin/FeedbackCreate').then(m => ({ default: m.FeedbackCreate })))

// Lazy loaded onboarding
const Onboarding = lazy(() => import('./pages/Onboarding').then(m => ({ default: m.Onboarding })))
const GetStarted = lazy(() => import('./pages/GetStarted').then(m => ({ default: m.GetStarted })))

// Lazy loaded guided mode
const GuidedModePage = lazy(() => import('./pages/GuidedModePage').then(m => ({ default: m.GuidedModePage })))

// Component to auto-redirect new users to get-started choice screen
function OnboardingRedirect() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading, user } = useAuth()
  const hasCompletedOnboarding = useHasCompletedOnboarding()

  useEffect(() => {
    // Skip redirect if user has preference to go directly to dashboard
    if (user?.skip_onboarding_redirect) {
      return
    }

    // Only redirect if:
    // 1. User is authenticated and not loading
    // 2. User has NOT completed onboarding
    // 3. User is on /app routes (main dashboard pages)
    // 4. User is NOT already on get-started, onboarding, or chat pages
    // 5. User is NOT in admin routes
    const isOnAppDashboard = location.pathname.startsWith('/app') &&
                              !location.pathname.startsWith('/app/get-started') &&
                              !location.pathname.startsWith('/app/onboarding') &&
                              !location.pathname.startsWith('/app/chat') &&
                              !location.pathname.startsWith('/app/guided-mode')
    const isAdmin = location.pathname.startsWith('/admin')

    // Also check localStorage directly as fallback (React state may lag behind)
    const userId = user?.id
    const localStorageCompleted = userId && (
      localStorage.getItem(`brazilnut_onboarding_completed_${userId}`) === 'true' ||
      localStorage.getItem(`brazilnut_onboarding_skipped_${userId}`) === 'true'
    )

    if (
      !isLoading &&
      isAuthenticated &&
      !hasCompletedOnboarding &&
      !localStorageCompleted &&
      isOnAppDashboard &&
      !isAdmin
    ) {
      navigate('/app/get-started', { replace: true })
    }
  }, [isAuthenticated, isLoading, hasCompletedOnboarding, location.pathname, navigate, user])

  return null
}

function AppContent() {
  return (
    <>
      <OnboardingRedirect />
      <Routes>
        {/* Marketing landing page - public access */}
        <Route path="/" element={
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Landing />
            </Suspense>
          </RouteErrorBoundary>
        } />

        {/* Legal pages - public access */}
        <Route path="/terms" element={
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Terms />
            </Suspense>
          </RouteErrorBoundary>
        } />
        <Route path="/privacy" element={
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Privacy />
            </Suspense>
          </RouteErrorBoundary>
        } />
        <Route path="/legal/dpa" element={
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <DataProcessingAgreement />
            </Suspense>
          </RouteErrorBoundary>
        } />
        <Route path="/security/access-policy" element={
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <AccessPolicy />
            </Suspense>
          </RouteErrorBoundary>
        } />

        {/* Pricing page - public access */}
        <Route path="/pricing" element={
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Pricing />
            </Suspense>
          </RouteErrorBoundary>
        } />

        {/* Security page - public access */}
        <Route path="/security" element={
          <RouteErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Security />
            </Suspense>
          </RouteErrorBoundary>
        } />

        {/* Public auth routes - redirect to dashboard if authenticated */}
        <Route element={<PublicRoute />}>
          <Route path="/login" element={
            <RouteErrorBoundary>
              <Login />
            </RouteErrorBoundary>
          } />
          <Route path="/signup" element={
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Signup />
              </Suspense>
            </RouteErrorBoundary>
          } />
        </Route>

        {/* OAuth callback - accessible regardless of auth state */}
        <Route path="/auth/google/callback" element={
          <RouteErrorBoundary>
            <GoogleCallback />
          </RouteErrorBoundary>
        } />
        {/* OTP sign-in approval landing (from the email button) - works signed in or out */}
        <Route path="/auth/approve" element={
          <RouteErrorBoundary>
            <Approve />
          </RouteErrorBoundary>
        } />

        {/* Protected routes - require authentication */}
        <Route element={<ProtectedRoute />}>
          {/* Get Started - choice screen for new users */}
          <Route path="/app/get-started" element={
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <GetStarted />
              </Suspense>
            </RouteErrorBoundary>
          } />
          <Route path="/app/onboarding" element={
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Onboarding />
              </Suspense>
            </RouteErrorBoundary>
          } />
          {/* Chat Mode - full page, no dashboard layout */}
          <Route path="/app/chat" element={
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <ChatMode />
              </Suspense>
            </RouteErrorBoundary>
          } />
          {/* Guided Mode - full page wizard, no dashboard layout */}
          <Route path="/app/guided-mode" element={
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <GuidedModePage />
              </Suspense>
            </RouteErrorBoundary>
          } />
          <Route path="/app" element={<DashboardLayout />}>
            <Route index element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Home />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="dashboard" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Dashboard />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="models" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Models />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="models/new" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <ModelCreate />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="models/:id" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <ModelDetail />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="models/:id/monitoring" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <ModelMonitoring />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="models/:modelId/insights" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <InsightsDashboard />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="predictions" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Predictions />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="predictions/history" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <PredictionHistory />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="data-sources" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <DataSources />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="pixel" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <PixelSetup />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="analytics" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Analytics />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="performance" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <ModelPerformance />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="drift" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <DriftDashboard />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="sla" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <SLADashboard />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="settings" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Settings />
                </Suspense>
              </RouteErrorBoundary>
            } />
          </Route>
          <Route path="/oauth/callback" element={
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <OAuthCallback />
              </Suspense>
            </RouteErrorBoundary>
          } />
        </Route>

        {/* Super admin routes */}
        <Route element={<ProtectedRoute requiredRole="super_admin" />}>
          <Route path="/admin" element={<DashboardLayout />}>
            <Route index element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <SuperAdminDashboard />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="tenants" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <TenantsList />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="tenants/new" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <TenantCreateForm />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="tenants/:id" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <TenantDetail />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="plans" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <PlansManagement />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="users" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <GlobalUserSearch />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="feedback" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <FeedbackList />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="feedback/new" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <FeedbackCreate />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="feedback/:id" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <FeedbackDetail />
                </Suspense>
              </RouteErrorBoundary>
            } />
          </Route>
        </Route>

        {/* Catch all - redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </>
  )
}

function App() {
  return (
    <ErrorBoundary level="app" showHomeButton={false} showResetButton={true}>
      <AuthProvider>
        <ThemeProvider defaultTheme="dark">
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
